import '../../style/chart/CanvasContent.scss';
import React, {useEffect, useMemo, useRef, useState, useCallback} from 'react';
import proj4 from 'proj4';
import * as turf from '@turf/turf';
import {getAmountWidth, getColor} from '../../services/chart/Functions';
import {useAppDispatch, useAppSelector} from '../../redux/hooks';
import {setMinMax, setSelectedItem} from '../../redux/slices/ChartStateSlice';
import {elementKey, unitSuffix} from '../../functions/drillholeTransform';

// ─── Types ────────────────────────────────────────────────────────────────────

type Props = {
    resetView: boolean;
    selectedBoreholes?: any[];
    onCameraChange?: (camera: {
        position: [number, number, number];
        zoom: number;
        target: [number, number, number]
    }) => void;
    cameraState?: { position: [number, number, number]; zoom: number; target: [number, number, number] } | null;
    nnOffset?: number;
    // Visual stroke width (in SVG units before zoom) of the colored borehole
    // segments. The underlying black drill-path border scales with it so the
    // outline ratio stays roughly the same.
    boreholeWidth?: number;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function interpolateAlongDrillPath(dripos: any[], targetZ: number) {
    for (let i = 1; i < dripos.length; i++) {
        const prevZ = dripos[i - 1].z;
        const currZ = dripos[i].z;
        if ((targetZ <= prevZ && targetZ >= currZ) || (targetZ >= prevZ && targetZ <= currZ)) {
            const t = (targetZ - prevZ) / (currZ - prevZ);
            return {
                x: dripos[i - 1].x + t * (dripos[i].x - dripos[i - 1].x),
                y: dripos[i - 1].y + t * (dripos[i].y - dripos[i - 1].y),
            };
        }
    }
    if (dripos.length > 0) {
        const first = dripos[0];
        const last = dripos[dripos.length - 1];
        return Math.abs(targetZ - first.z) < Math.abs(targetZ - last.z)
            ? {x: first.x, y: first.y}
            : {x: last.x, y: last.y};
    }
    return {x: 0, y: 0};
}

function interpolateAlongDrillPathByDepth(dripos: any[], targetDepth: number) {
    for (let i = 1; i < dripos.length; i++) {
        const prevD = dripos[i - 1].depth ?? 0;
        const currD = dripos[i].depth ?? 0;
        if (targetDepth >= prevD && targetDepth <= currD) {
            const span = currD - prevD;
            const t = span !== 0 ? (targetDepth - prevD) / span : 0;
            return {
                relX: dripos[i - 1].relX + t * (dripos[i].relX - dripos[i - 1].relX),
                relY: dripos[i - 1].relY + t * (dripos[i].relY - dripos[i - 1].relY),
                relZ: dripos[i - 1].relZ + t * (dripos[i].relZ - dripos[i - 1].relZ),
            };
        }
    }
    return null;
}

function interpolateDepthAlongDrillPath(dripos: any[], targetZ: number): number {
    for (let i = 1; i < dripos.length; i++) {
        const prevZ = dripos[i - 1].z;
        const currZ = dripos[i].z;
        if ((targetZ <= prevZ && targetZ >= currZ) || (targetZ >= prevZ && targetZ <= currZ)) {
            const t = (targetZ - prevZ) / (currZ - prevZ);
            const prevDepth = dripos[i - 1].depth ?? Math.abs(dripos[i - 1].z);
            const currDepth = dripos[i].depth ?? Math.abs(dripos[i].z);
            return prevDepth + t * (currDepth - prevDepth);
        }
    }
    if (dripos.length > 0) {
        const first = dripos[0];
        const last = dripos[dripos.length - 1];
        return Math.abs(targetZ - first.z) < Math.abs(targetZ - last.z)
            ? (first.depth ?? Math.abs(first.z))
            : (last.depth ?? Math.abs(last.z));
    }
    return Math.abs(targetZ);
}

function measure(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6378.137;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 1000;
}

// ─── Main Component ───────────────────────────────────────────────────────────

const CanvasContent = (props: Props) => {
    const boreholeStroke = props.boreholeWidth ?? 7;
    const drillPathStroke = boreholeStroke + 3;
    const dispatch = useAppDispatch();
    const activeElement = useAppSelector((s: any) => s.oldDesignState.activeElement);
    const colorTable = useAppSelector((s: any) => s.oldDesignState.colorTable);
    const colorTableXrf = useAppSelector((s: any) => s.oldDesignState.colorTableXrf);
    const colorTableMinerals = useAppSelector((s: any) => s.oldDesignState.colorTableMinerals);
    const rawCanvasElements = useAppSelector((s: any) => s.oldDesignState.elements);
    const chartActiveOverrides = useAppSelector((s: any) => s.oldDesignState.chartActiveOverrides) as Record<string, boolean>;
    const colorByElementXrf = useAppSelector((s: any) => s.oldDesignState.colorByElementXrf) as boolean;
    const colorByElementMinerals = useAppSelector((s: any) => s.oldDesignState.colorByElementMinerals) as boolean;
    const elements = useMemo(
        () => rawCanvasElements.map((el: any) => {
            const key = elementKey(el);
            const overridden = Object.prototype.hasOwnProperty.call(chartActiveOverrides ?? {}, key);
            return overridden ? {...el, active: !!chartActiveOverrides[key]} : el;
        }),
        [rawCanvasElements, chartActiveOverrides]
    );
    const dataSources = useAppSelector((s: any) => s.oldDesignState.dataSources || []);
    const minMax = useAppSelector((s: any) => s.oldDesignState.minMax);
    const selectedItem = useAppSelector((s: any) => s.oldDesignState.selectedItem);
    const selectLine = useAppSelector((s: any) => s.oldDesignState.selectLine);
    const selectedBoreholes = props.selectedBoreholes ?? [];
    const nnOffset = props.nnOffset ?? 0;

    // ── SVG pan/zoom/rotate state ─────────────────────────────────────────────
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [transform, setTransform] = useState({x: 80, y: 80, scale: 1, rotationY: 0});
    const [svgSize, setSvgSize] = useState({width: 0, height: 0});
    const isPanning = useRef(false);
    const isRotating = useRef(false);
    const panStart = useRef({x: 0, y: 0});
    const rotateStart = useRef({x: 0, rotationY: 0});
    const transformRef = useRef(transform);
    transformRef.current = transform;
    const initialFitDone = useRef(false);
    const prevResetView = useRef(props.resetView);

    // Track SVG size for viewport culling. Uses a callback ref so we observe
    // the element exactly when it mounts (the component shows a spinner before
    // sceneData arrives, so the SVG is mounted only later — a plain `[]`
    // useEffect on svgRef would fire before the SVG exists and never re-attach).
    const setSvgRef = useCallback((el: SVGSVGElement | null) => {
        svgRef.current = el;
    }, []);
    const [svgEl, setSvgEl] = useState<SVGSVGElement | null>(null);
    const combinedSvgRef = useCallback((el: SVGSVGElement | null) => {
        setSvgRef(el);
        setSvgEl(el);
    }, [setSvgRef]);

    useEffect(() => {
        if (!svgEl) return;
        const update = () => {
            const r = svgEl.getBoundingClientRect();
            setSvgSize(prev => (prev.width === r.width && prev.height === r.height)
                ? prev
                : {width: r.width, height: r.height});
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(svgEl);
        return () => ro.disconnect();
    }, [svgEl]);

    // ── Tooltip state ─────────────────────────────────────────────────────────
    const [tooltip, setTooltip] = useState<{ x: number; y: number; content: React.ReactNode } | null>(null);

    // ── Derived element lookups ───────────────────────────────────────────────
    const elementBySourceAndId = useMemo(() => {
        const map = new Map<string, any>();
        if (!elements) return map;
        for (const e of elements) map.set(`${e.source ?? 'xrf'}::${e.id}`, e);
        return map;
    }, [elements]);

    const activeElementIdsBySource = useMemo(() => {
        const map = new Map<string, Set<string>>();
        if (!elements) return map;
        for (const e of elements) {
            if (!e.active) continue;
            const src = e.source ?? 'xrf';
            if (!map.has(src)) map.set(src, new Set());
            map.get(src)!.add(e.id);
        }
        return map;
    }, [elements]);

    // ── Scene data ────────────────────────────────────────────────────────────
    const sceneData = useMemo(() => {
        if (!selectLine || selectedBoreholes.length === 0) return null;

        const coords1Map = proj4('EPSG:4326', 'EPSG:3857', [selectLine.x1, selectLine.y1]);
        const coords2Map = proj4('EPSG:4326', 'EPSG:3857', [selectLine.x2, selectLine.y2]);

        const lineVec = {
            x: coords2Map[0] - coords1Map[0],
            y: coords2Map[1] - coords1Map[1],
        };
        const lineLen = Math.sqrt(lineVec.x ** 2 + lineVec.y ** 2) || 1;
        const lineUnit = {x: lineVec.x / lineLen, y: lineVec.y / lineLen};

        // Perpendicular vector for calculating horizontal deviation in chart view
        const perpUnit = {x: -lineUnit.y, y: lineUnit.x};

        // Line bearing
        const bearing = turf.bearing(
            turf.point([selectLine.x1, selectLine.y1]),
            turf.point([selectLine.x2, selectLine.y2])
        );

        // Camera viewing direction (perpendicular to the line)
        // Requested logic:
        // L -> R (90°): View North (0°)
        // R -> L (270°): View South (180°)
        // Top -> Bottom (180°): View East (90°)
        // Bottom -> Top (0°): View West (270°)
        // All these satisfy: View = (LineBearing - 90)
        const viewingDirection = ((bearing - 90) % 360 + 360) % 360;
        // The windrose image has North at the top (0°).
        // If we look in 'viewingDirection', North is at -viewingDirection relative to our view.
        const windroseAngle = (360 - viewingDirection) % 360;

        const endPos = {
            x: coords1Map[0] + lineLen * lineUnit.x,
            y: coords1Map[1] + lineLen * lineUnit.y,
        };
        const centerPos = {
            x: (coords1Map[0] + endPos.x) / 2,
            y: (coords1Map[1] + endPos.y) / 2,
        };

        // Process boreholes
        let maxAlt = 0;
        let minRelZ = 0;
        let maxRelZ = 0;
        const boreholes = selectedBoreholes.map((bh: any) => {
            let dripos = bh.dri_dripos ? bh.dri_dripos.map((p: any) => ({...p})) : [];

            // Generate default vertical dripos if missing
            if (dripos.length === 0) {
                let maxDepth = 0;
                for (const mea of (bh.dri_mea || [])) {
                    for (const xrf of (mea.mea_xrfres || [])) {
                        for (const spe of (xrf.xrfres_spe || [])) {
                            if (spe.z2 && Math.abs(spe.z2) > maxDepth) maxDepth = Math.abs(spe.z2);
                        }
                    }
                }
                if (maxDepth === 0) maxDepth = 100;
                dripos = [
                    {x: 0, y: 0, z: 0, depth: 0, height_label: 0},
                    {x: 0, y: 0, z: -maxDepth, depth: maxDepth, height_label: -maxDepth},
                ];
            }

            dripos.sort((a: any, b: any) => a.depth - b.depth);

            for (const p of dripos) {
                if (Math.abs(p.z) > maxAlt) maxAlt = Math.abs(p.z);
            }

            // Vertical shift so that the line at relZ=0 represents nnOffset above sea level.
            // Positive elevationOffset => borehole top sits ABOVE the line (negative relZ).
            const elevationOffset = (bh.elevation ?? 0) - nnOffset;

            // Calculate 3D coordinates relative to profile center in EPSG:3857 (meters)
            // p.x, p.y from transformDrillhole are relative to coords1Map
            const dripos3D = dripos.map((p: any) => {
                const relZ = Math.abs(p.z) - elevationOffset;
                if (relZ < minRelZ) minRelZ = relZ;
                if (relZ > maxRelZ) maxRelZ = relZ;
                return {
                    ...p,
                    relX: (coords1Map[0] + p.x) - centerPos.x,
                    relY: (coords1Map[1] + p.y) - centerPos.y,
                    relZ,
                };
            });

            // Process measurements with 3D coordinates
            const dri_mea_3d = (bh.dri_mea || []).map((mea: any) => ({
                ...mea,
                mea_xrfres: (mea.mea_xrfres || []).map((xrf: any) => ({
                    ...xrf,
                    xrfres_spe: (xrf.xrfres_spe || []).map((spe: any) => {
                        const interp1 = interpolateAlongDrillPath(dripos, spe.z1);
                        const interp2 = interpolateAlongDrillPath(dripos, spe.z2);
                        return {
                            ...spe,
                            interp1: {
                                relX: (coords1Map[0] + interp1.x) - centerPos.x,
                                relY: (coords1Map[1] + interp1.y) - centerPos.y,
                                relZ: Math.abs(spe.z1) - elevationOffset
                            },
                            interp2: {
                                relX: (coords1Map[0] + interp2.x) - centerPos.x,
                                relY: (coords1Map[1] + interp2.y) - centerPos.y,
                                relZ: Math.abs(spe.z2) - elevationOffset
                            }
                        };
                    })
                }))
            }));

            return {...bh, dri_dripos: dripos3D, dri_mea: dri_mea_3d};
        });

        // maxDist should be the total length of the profile line
        const maxDist = lineLen;

        return {boreholes, maxAlt, minRelZ, maxRelZ, maxDist, lineLen, lineUnit, perpUnit, viewingDirection, coords1Map, centerPos};
    }, [selectLine, selectedBoreholes, nnOffset]);

    // Dispatch minMax when sceneData changes
    useEffect(() => {
        if (sceneData) {
            dispatch(setMinMax({
                maxAlt: sceneData.maxAlt,
                maxDistance: sceneData.maxDist,
                factor: minMax?.factor ?? 1,
            }));
        }
    }, [sceneData]);

    // ── Reset view ────────────────────────────────────────────────────────────
    useEffect(() => {
        const resetExplicit = prevResetView.current !== props.resetView;
        prevResetView.current = props.resetView;

        if (!sceneData || !svgRef.current) {
            initialFitDone.current = false;
            setTransform({x: 80, y: 80, scale: 1, rotationY: 0});
            return;
        }

        // Only fit on first load or explicit reset — not on data source changes
        if (initialFitDone.current && !resetExplicit) return;

        const {maxDist, minRelZ, maxRelZ} = sceneData;
        const factor = minMax?.factor ?? 1;
        // Always include the NN line (relZ=0) in the fit so it's visible.
        const fitTop = Math.min(minRelZ, 0);
        const fitBottom = Math.max(maxRelZ, 0);
        const verticalExtent = (fitBottom - fitTop) * factor;
        const verticalMid = ((fitTop + fitBottom) / 2) * factor;

        const rect = svgRef.current.getBoundingClientRect();
        const padding = 100;
        const availableWidth = Math.max(100, rect.width - padding);
        const availableHeight = Math.max(100, rect.height - padding);

        const scaleX = availableWidth / (maxDist || 1);
        const scaleY = availableHeight / (verticalExtent || 1);
        const newScale = Math.min(scaleX, scaleY, 2);

        setTransform({
            x: rect.width / 2,
            y: rect.height / 2 - verticalMid * newScale,
            scale: newScale,
            rotationY: 0,
        });
        initialFitDone.current = true;
    }, [props.resetView, sceneData]);

    // Restore camera from template
    useEffect(() => {
        if (props.cameraState) {
            setTransform({
                x: props.cameraState.position[0],
                y: props.cameraState.position[1],
                scale: props.cameraState.zoom,
                rotationY: props.cameraState.position[2] || 0, // Abuse Z for rotation
            });
        }
    }, [props.cameraState]);

    // Report camera changes
    useEffect(() => {
        props.onCameraChange?.({
            position: [transform.x, transform.y, transform.rotationY],
            zoom: transform.scale,
            target: [0, 0, 0],
        });
    }, [transform]);

    // ── Mouse handlers ────────────────────────────────────────────────────────
    const onMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.shiftKey || e.button === 1) {
            isRotating.current = true;
            rotateStart.current = {x: e.clientX, rotationY: transformRef.current.rotationY};
        } else if (e.button === 0 || e.button === 2) {
            isPanning.current = true;
            panStart.current = {x: e.clientX - transformRef.current.x, y: e.clientY - transformRef.current.y};
        }
        e.preventDefault();
    }, []);

    const onMouseMove = useCallback((e: React.MouseEvent) => {
        if (isPanning.current) {
            setTransform(t => ({...t, x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y}));
        } else if (isRotating.current) {
            const dx = e.clientX - rotateStart.current.x;
            setTransform(t => ({...t, rotationY: rotateStart.current.rotationY + dx * 0.01}));
        }
    }, []);

    const onMouseUp = useCallback(() => {
        isPanning.current = false;
        isRotating.current = false;
    }, []);

    // ── Zoom handler ──────────────────────────────────────────────────────────
    const onWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
        const rect = svgRef.current!.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        setTransform(t => {
            const newScale = Math.max(0.01, Math.min(100, t.scale * zoomFactor));
            const ratio = newScale / t.scale;
            return {
                ...t,
                scale: newScale,
                x: mx - ratio * (mx - t.x),
                y: my - ratio * (my - t.y),
            };
        });
    }, []);

    // ── 3D Projection ────────────────────────────────────────────────────────
    const project = useCallback((relX: number, relY: number, relZ: number, f: number) => {
        // Rotation around Y axis
        const cosY = Math.cos(transform.rotationY);
        const sinY = Math.sin(transform.rotationY);

        // Project relative 3D coordinates (meters) to 2D view space
        // y in 3D is depth (relZ), x and y in horizontal plane (relX, relY)
        // We rotate relX and relY around the center (which is already 0,0 for relX/relY)
        const rotX = relX * cosY - relY * sinY;
        // const rotY = relX * sinY + relY * cosY; // depth in scene, not used for 2D projection

        return {
            svgX: rotX,
            svgY: relZ * f
        };
    }, [transform.rotationY]);

    const factor = minMax?.factor ?? 1;

    const stepSize = useMemo(() => {
        const targetPixels = 100; // Optimal vertical distance between markers in pixels
        const rawStep = targetPixels / (factor * transform.scale);
        const niceSteps = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 25, 50, 100, 200, 500, 1000];
        for (const s of niceSteps) {
            if (s >= rawStep) return s;
        }
        return 1000;
    }, [factor, transform.scale]);

    // ── Viewport bounds in world (SVG-inner) coordinates for culling ──────────
    // Anything outside this box (with padding) is skipped during render.
    const viewportBounds = useMemo(() => {
        if (svgSize.width === 0 || svgSize.height === 0) return null;
        const padPx = 120; // generous so bars sticking out laterally still draw
        return {
            left: (-padPx - transform.x) / transform.scale,
            right: (svgSize.width + padPx - transform.x) / transform.scale,
            top: (-padPx - transform.y) / transform.scale,
            bottom: (svgSize.height + padPx - transform.y) / transform.scale,
        };
    }, [svgSize, transform.x, transform.y, transform.scale]);

    // LoD threshold: when a single segment is shorter than this many SVG units
    // (≈ pixels-before-zoom), we start merging consecutive bars together.
    // Triggered only when more than three boreholes are visible.
    const lodSegmentMinSize = useMemo(() => {
        return 1.5 / transform.scale;
    }, [transform.scale]);

    // ── Delegated mouse handling ──────────────────────────────────────────────
    // Per-element onMouseEnter/onMouseLeave is a major React perf hit when
    // there are many SVG nodes (React installs a native listener per node
    // because mouseenter/leave don't bubble). We delegate to the SVG root and
    // look bars up by data-bar-id via a ref-held map.
    const barDataRef = useRef<Map<string, any>>(new Map());

    const showBarTooltip = useCallback((id: string, clientX: number, clientY: number) => {
        const entry = barDataRef.current.get(id);
        if (!entry) return;
        const {amount, currentElement, barColor, depthFrom, depthTo, spe} = entry;
        const tooltipName = currentElement?.name ?? `#${amount.element_id}`;
        const tooltipValue = (amount.value?.toFixed(2).replace('.', ',') ?? '0,00') + unitSuffix(currentElement?.unit);
        setTooltip({
            x: clientX, y: clientY,
            content: (
                <div>
                    <div style={{fontSize: 11, color: 'var(--text-color-secondary)'}}>
                        Depth (MD): {depthFrom.toFixed(2).replace('.', ',')} – {depthTo.toFixed(2).replace('.', ',')} m
                    </div>
                    <div style={{fontSize: 11, color: 'var(--text-color-secondary)'}}>
                        Depth (TVD): {Math.min(spe.interp1.relZ, spe.interp2.relZ).toFixed(2).replace('.', ',')} – {Math.max(spe.interp1.relZ, spe.interp2.relZ).toFixed(2).replace('.', ',')} m
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                        <div style={{width: 10, height: 10, background: currentElement?.color ?? barColor, borderRadius: 2}}/>
                        <span style={{fontWeight: 'bold'}}>{tooltipName}</span>
                        <span>{tooltipValue}</span>
                    </div>
                </div>
            )
        });
    }, []);

    const onSvgMouseOver = useCallback((e: React.MouseEvent) => {
        const t = e.target as Element;
        const id = t.getAttribute && t.getAttribute('data-bar-id');
        if (id) showBarTooltip(id, e.clientX, e.clientY);
    }, [showBarTooltip]);

    const onSvgMouseOut = useCallback((e: React.MouseEvent) => {
        const t = e.target as Element;
        if (t.getAttribute && t.getAttribute('data-bar-id')) {
            setTooltip(null);
        }
    }, []);

    const onSvgClick = useCallback((e: React.MouseEvent) => {
        const t = e.target as Element;
        const id = t.getAttribute && t.getAttribute('data-bar-id');
        if (!id) return;
        const entry = barDataRef.current.get(id);
        if (!entry) return;
        dispatch(setSelectedItem({
            spectrum: entry.spe, amount: entry.amount, parent: entry.xrf,
            greatest_amount_value: entry.sourceMax,
            source: entry.meaSource, depthFrom: entry.depthFrom, depthTo: entry.depthTo,
        }));
    }, [dispatch]);

    // ── Render ────────────────────────────────────────────────────────────────
    if (!sceneData || !minMax) {
        return (
            <div style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <i className="pi pi-spin pi-spinner" style={{fontSize: '3rem', color: '#666'}}/>
            </div>
        );
    }

    const {
        boreholes,
        maxAlt,
        minRelZ,
        maxRelZ,
        maxDist,
        lineUnit,
        perpUnit,
        viewingDirection,
        coords1Map,
        lineLen,
        centerPos
    } = sceneData;

    // Grid lines every stepSize. Negative values are above the NN line, positive are below.
    const gridSteps: number[] = [];
    const gridTop = Math.floor(minRelZ / stepSize) * stepSize;
    const gridBottom = Math.ceil(maxRelZ / stepSize) * stepSize + stepSize;
    for (let d = gridTop; d <= gridBottom; d += stepSize) {
        if (d === 0) continue; // skip the NN line itself (rendered separately as the surface line)
        gridSteps.push(d);
    }

    const currentViewingDirection = (viewingDirection + transform.rotationY * 180 / Math.PI) % 360;
    const currentWindroseAngle = (360 - currentViewingDirection) % 360;

    // Z-order boreholes by their depth-into-screen so rotation correctly swaps front/back.
    // Depth axis = relX*sin(rot) + relY*cos(rot); larger value = further from viewer → render first.
    const sortedBoreholes = (() => {
        const cosY = Math.cos(transform.rotationY);
        const sinY = Math.sin(transform.rotationY);
        return boreholes
            .filter((bh: any) => bh.dri_dripos?.length > 0 && bh.dri_mea)
            .slice()
            .sort((a: any, b: any) => {
                const aTop = a.dri_dripos[0];
                const bTop = b.dri_dripos[0];
                const aDepth = aTop.relX * sinY + aTop.relY * cosY;
                const bDepth = bTop.relX * sinY + bTop.relY * cosY;
                return bDepth - aDepth;
            });
    })();

    // ── Pre-compute per-borehole render data ──────────────────────────────────
    // Goals: project once, cull bars outside the viewport, merge adjacent bars
    // when zoomed out (LoD), and register each bar in a ref-held Map for
    // delegated mouse handling (avoids per-element React listeners).
    const lodEnabled = sortedBoreholes.length > 3;
    const bothSources = activeElementIdsBySource.size >= 2;
    // Lateral padding (world units) for cull tests — covers amount bars sticking
    // out sideways and the borehole stroke half-width.
    const lateralPad = (80 + boreholeStroke) / transform.scale;

    barDataRef.current = new Map();

    type RawBar = {
        spe: any; amount: any; xrf: any; meaSource: string;
        currentElement: any; bestElement: any; best: any;
    };
    type RenderBar = RawBar & {
        p1: {svgX: number; svgY: number}; p2: {svgX: number; svgY: number};
        id: string;
        barColor: string; boreholeColor: string;
        sourceMax: number; barWidthPx: number;
        depthFrom: number; depthTo: number;
        isSelected: boolean;
        merged: boolean;
    };

    const boreholeRenderData: Array<{
        bh: any; bhIdx: number; dripos: any[];
        pathPoints: Array<{svgX: number; svgY: number}>; pathD: string;
        finalBars: RenderBar[];
        maxDepth: number;
    }> = [];

    for (let bhIdx = 0; bhIdx < sortedBoreholes.length; bhIdx++) {
        const bh = sortedBoreholes[bhIdx];
        const dripos = bh.dri_dripos;
        const pathPoints = dripos.map((p: any) => project(p.relX, p.relY, p.relZ, factor));

        // Whole-borehole bbox cull
        if (viewportBounds) {
            let bxmin = Infinity, bxmax = -Infinity, bymin = Infinity, bymax = -Infinity;
            for (const p of pathPoints) {
                if (p.svgX < bxmin) bxmin = p.svgX;
                if (p.svgX > bxmax) bxmax = p.svgX;
                if (p.svgY < bymin) bymin = p.svgY;
                if (p.svgY > bymax) bymax = p.svgY;
            }
            if (bxmax + lateralPad < viewportBounds.left
                || bxmin - lateralPad > viewportBounds.right
                || bymax < viewportBounds.top
                || bymin > viewportBounds.bottom) {
                continue;
            }
        }

        const pathD = pathPoints.map((p: any, i: number) => `${i === 0 ? 'M' : 'L'}${p.svgX},${p.svgY}`).join(' ');

        // Build raw bars (same logic as before)
        const rawBars: RawBar[] = [];
        for (const mea of bh.dri_mea) {
            const meaSource = mea.__source ?? 'xrf';
            if (!activeElementIdsBySource.has(meaSource)) continue;
            const activeElementIds = activeElementIdsBySource.get(meaSource)!;
            for (const xrf of mea.mea_xrfres) {
                for (const spe of xrf.xrfres_spe) {
                    let amount: any = undefined;
                    let best: any = null;
                    for (const speres of (spe.spe_speres || [])) {
                        if (!activeElementIds.has(speres.element_id)) continue;
                        if (speres.value > 0 && (!best || speres.value > best.value)) best = speres;
                    }
                    if (activeElement && (activeElement.source ?? 'xrf') === meaSource) {
                        amount = spe.spe_speres.find((x: any) => x.element_id === activeElement.id);
                    } else {
                        amount = best;
                    }
                    if (!amount || amount.value <= 0) continue;
                    const currentElement = elementBySourceAndId.get(`${meaSource}::${amount.element_id}`) ?? null;
                    if (currentElement && !currentElement.active) continue;
                    const bestElement = best ? elementBySourceAndId.get(`${meaSource}::${best.element_id}`) : null;
                    rawBars.push({spe, amount, xrf, meaSource, currentElement, bestElement, best});
                }
            }
        }

        // Project + per-bar cull
        const projected: RenderBar[] = [];
        for (const bar of rawBars) {
            const p1 = project(bar.spe.interp1.relX, bar.spe.interp1.relY, bar.spe.interp1.relZ, factor);
            const p2 = project(bar.spe.interp2.relX, bar.spe.interp2.relY, bar.spe.interp2.relZ, factor);
            if (viewportBounds) {
                const minX = Math.min(p1.svgX, p2.svgX) - lateralPad;
                const maxX = Math.max(p1.svgX, p2.svgX) + lateralPad;
                const minY = Math.min(p1.svgY, p2.svgY);
                const maxY = Math.max(p1.svgY, p2.svgY);
                if (maxX < viewportBounds.left || minX > viewportBounds.right
                    || maxY < viewportBounds.top || minY > viewportBounds.bottom) {
                    continue;
                }
            }
            projected.push({...bar, p1, p2} as RenderBar);
        }

        // LoD merge: group adjacent bars per source whose combined vertical
        // extent (in screen pixels before zoom) is smaller than the threshold.
        // The dominant bar (highest amount) carries the color/value; the
        // segment spans first.p1 → last.p2 so it remains visually contiguous.
        let finalBars: RenderBar[] = projected;
        if (lodEnabled) {
            const bySource = new Map<string, RenderBar[]>();
            for (const b of projected) {
                let arr = bySource.get(b.meaSource);
                if (!arr) { arr = []; bySource.set(b.meaSource, arr); }
                arr.push(b);
            }
            const mergedAll: RenderBar[] = [];
            bySource.forEach(srcBars => {
                srcBars.sort((a, b) => Math.min(a.p1.svgY, a.p2.svgY) - Math.min(b.p1.svgY, b.p2.svgY));
                let i = 0;
                while (i < srcBars.length) {
                    const start = srcBars[i];
                    const yTop = Math.min(start.p1.svgY, start.p2.svgY);
                    let j = i + 1;
                    let end = start;
                    while (j < srcBars.length) {
                        const next = srcBars[j];
                        const nextYBot = Math.max(next.p1.svgY, next.p2.svgY);
                        if (nextYBot - yTop > lodSegmentMinSize) break;
                        end = next;
                        j++;
                    }
                    if (j - i === 1) {
                        mergedAll.push(start);
                    } else {
                        let dominant = start;
                        for (let k = i + 1; k < j; k++) {
                            if (srcBars[k].amount.value > dominant.amount.value) dominant = srcBars[k];
                        }
                        mergedAll.push({
                            ...dominant,
                            p1: start.p1,
                            p2: end.p2,
                            merged: true,
                        });
                    }
                    i = j;
                }
            });
            finalBars = mergedAll;
        }

        // Decorate + register in delegation map
        for (const bar of finalBars) {
            const meaSource = bar.meaSource;
            const sourceColorTable = meaSource === 'minerals' ? colorTableMinerals : colorTableXrf;
            const activeColorTable = (sourceColorTable && sourceColorTable.length > 0) ? sourceColorTable : colorTable;
            const useElementColor = meaSource === 'minerals' ? colorByElementMinerals : colorByElementXrf;
            bar.barColor = useElementColor
                ? (bar.bestElement?.color ?? bar.currentElement?.color ?? '#999999')
                : getColor(bar.amount.value, activeColorTable).color;
            bar.boreholeColor = useElementColor
                ? (bar.bestElement?.color ?? bar.currentElement?.color ?? '#999999')
                : ((bar.bestElement?.color) ?? (bar.best ? getColor(bar.best.value, activeColorTable).color : getColor(bar.amount.value, activeColorTable).color));
            bar.sourceMax = meaSource === 'minerals'
                ? (bh.greatest_amount_value_minerals || bh.greatest_amount_value || 1)
                : (bh.greatest_amount_value_xrf || bh.greatest_amount_value || 1);
            bar.barWidthPx = getAmountWidth(bar.sourceMax, bar.amount.value) + 10;
            const depth1 = interpolateDepthAlongDrillPath(dripos, bar.spe.z1);
            const depth2 = interpolateDepthAlongDrillPath(dripos, bar.spe.z2);
            bar.depthFrom = Math.min(depth1, depth2);
            bar.depthTo = Math.max(depth1, depth2);
            bar.isSelected = bar.spe === selectedItem?.spectrum;
            bar.id = `${bhIdx}-${meaSource}-${bar.spe.xrf_result_id ?? bar.spe.id}${bar.merged ? `-m${bar.p1.svgY.toFixed(2)}` : ''}`;
            barDataRef.current.set(bar.id, {
                spe: bar.spe, amount: bar.amount, xrf: bar.xrf,
                meaSource, currentElement: bar.currentElement,
                barColor: bar.barColor, sourceMax: bar.sourceMax,
                depthFrom: bar.depthFrom, depthTo: bar.depthTo,
            });
        }

        const maxDepth = dripos[dripos.length - 1]?.depth ?? 0;
        boreholeRenderData.push({bh, bhIdx, dripos, pathPoints, pathD, finalBars, maxDepth});
    }

    // Label density: show borehole labels only when there's enough room between
    // them. Path-length labels only when their vertical pixel spacing > 30 px.
    const labelStride = (() => {
        const n = boreholeRenderData.length;
        if (n < 2) return 1;
        const pxPerBh = (maxDist * transform.scale) / n;
        if (pxPerBh > 60) return 1;
        if (pxPerBh > 30) return 2;
        if (pxPerBh > 15) return 4;
        return Math.ceil(60 / Math.max(pxPerBh, 1));
    })();
    const showPathLengthLabels = (stepSize * factor * transform.scale) > 25;

    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                cursor: isPanning.current ? 'grabbing' : 'grab'
            }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onContextMenu={e => e.preventDefault()}
        >
            {/* Windrose */}
            <img
                src="/windrose.png"
                style={{
                    position: 'absolute', top: 12, right: 12, width: 80, height: 80,
                    transform: `rotate(${currentWindroseAngle}deg)`,
                    zIndex: 10, pointerEvents: 'none', opacity: 0.85, borderRadius: 50
                }}
                alt="Windrose"
            />

            <svg
                ref={combinedSvgRef}
                style={{width: '100%', height: '100%', display: 'block'}}
                onWheel={onWheel}
                onMouseOver={onSvgMouseOver}
                onMouseOut={onSvgMouseOut}
                onClick={onSvgClick}
            >
                <g transform={`translate(${transform.x},${transform.y}) scale(${transform.scale})`}>

                    {/* Horizontal sea-level (NN) reference line */}
                    {(() => {
                        const pStart = project(-maxDist / 2, 0, 0, factor);
                        const pEnd = project(maxDist / 2, 0, 0, factor);
                        return <line x1={pStart.svgX} y1={pStart.svgY} x2={pEnd.svgX} y2={pEnd.svgY}
                                     stroke="var(--primary-color)" strokeWidth={2}
                                     vectorEffect="non-scaling-stroke" pointerEvents="none"/>;
                    })()}

                    {/* Distance label */}
                    {(() => {
                        const pStart = project(-maxDist / 2, 0, 0, factor);
                        return (
                            <text x={pStart.svgX} y={pStart.svgY - 10 / transform.scale} fill="var(--text-color)"
                                  fontSize={12 / transform.scale} textAnchor="start" pointerEvents="none">
                                {'← ' + maxDist.toFixed(0) + 'm →'}
                            </text>
                        );
                    })()}

                    {/* NN line altitude label on the far left */}
                    {(() => {
                        const pStart = project(-maxDist / 2, 0, 0, factor);
                        const altLabel = nnOffset % 1 === 0 ? nnOffset.toString() : nnOffset.toFixed(1);
                        return (
                            <text x={pStart.svgX - 8 / transform.scale} y={pStart.svgY}
                                  fill="var(--primary-color)" fontSize={10 / transform.scale}
                                  dominantBaseline="middle" textAnchor="end" fontWeight="bold"
                                  pointerEvents="none">
                                {altLabel + 'm'}
                            </text>
                        );
                    })()}

                    {/* Grid lines with altitude labels (actual elevation above sea level) on the far left */}
                    {gridSteps.map(d => {
                        const pStart = project(-maxDist / 2, 0, d, factor);
                        const pEnd = project(maxDist / 2, 0, d, factor);
                        const altitude = nnOffset - d;
                        const altLabel = altitude % 1 === 0 ? altitude.toString() : altitude.toFixed(1);
                        return (
                            <g key={d} pointerEvents="none">
                                <line x1={pStart.svgX} y1={pStart.svgY} x2={pEnd.svgX} y2={pEnd.svgY}
                                      stroke="var(--surface-border)" strokeWidth={1}
                                      strokeDasharray="4,4"
                                      vectorEffect="non-scaling-stroke"/>
                                <text x={pStart.svgX - 8 / transform.scale} y={pStart.svgY}
                                      fill="var(--text-color-secondary)" fontSize={10 / transform.scale}
                                      dominantBaseline="middle" textAnchor="end">
                                    {altLabel + 'm'}
                                </text>
                            </g>
                        );
                    })}

                    {/* Boreholes — uses pre-computed render data */}
                    {boreholeRenderData.map(({bh, bhIdx, dripos, pathPoints, pathD, finalBars, maxDepth}) => {
                        const showLabel = bhIdx % labelStride === 0;
                        return (
                            <g key={bh.hole_id ?? bhIdx}>
                                {/* Measurement amount bars — delegated mouse events on the SVG */}
                                {finalBars.map(bar => {
                                    const x1 = bar.p1.svgX, y1 = bar.p1.svgY;
                                    const x2 = bar.p2.svgX, y2 = bar.p2.svgY;
                                    const segVecX = x2 - x1;
                                    const segVecY = y2 - y1;
                                    const segLen = Math.sqrt(segVecX ** 2 + segVecY ** 2) || 1;
                                    const perpX = segVecY / segLen;
                                    const perpY = -segVecX / segLen;
                                    const sourceIdx = dataSources.indexOf(bar.meaSource);
                                    const directionFactor = sourceIdx === 0 ? -1 : 1;
                                    const finalPerpX = perpX * directionFactor;
                                    const finalPerpY = perpY * directionFactor;
                                    const startOff = (bar.isSelected ? (boreholeStroke + 2) / 2 : boreholeStroke / 2) / transform.scale;
                                    const barW = bar.barWidthPx / transform.scale;
                                    const pts = [
                                        [x1 + startOff * finalPerpX, y1 + startOff * finalPerpY],
                                        [x2 + startOff * finalPerpX, y2 + startOff * finalPerpY],
                                        [x2 + (startOff + barW) * finalPerpX, y2 + (startOff + barW) * finalPerpY],
                                        [x1 + (startOff + barW) * finalPerpX, y1 + (startOff + barW) * finalPerpY],
                                    ].map(([px, py]) => `${px},${py}`).join(' ');
                                    return (
                                        <polygon
                                            key={`bar-${bar.id}`}
                                            data-bar-id={bar.id}
                                            points={pts}
                                            fill={bar.barColor}
                                            stroke={bar.isSelected ? 'var(--primary-color)' : 'none'}
                                            strokeWidth={bar.isSelected ? 1 : 0}
                                            vectorEffect="non-scaling-stroke"
                                            opacity={0.8}
                                            style={{cursor: 'pointer'}}
                                        />
                                    );
                                })}

                                {/* Borehole label — only when there's room between neighboring boreholes */}
                                {showLabel && (
                                    <text x={pathPoints[0].svgX} y={pathPoints[0].svgY - 22 / transform.scale}
                                          fill="var(--text-color)"
                                          fontSize={11 / transform.scale} textAnchor="middle" fontWeight="bold"
                                          pointerEvents="none">
                                        {bh.id ?? bh.hole_id}
                                    </text>
                                )}
                                {showLabel && bh.elevation != null && (
                                    <text x={pathPoints[0].svgX} y={pathPoints[0].svgY - 10 / transform.scale}
                                          fill="var(--text-color-secondary)"
                                          fontSize={8 / transform.scale} textAnchor="middle"
                                          pointerEvents="none">
                                        {(Number(bh.elevation) % 1 === 0
                                            ? Number(bh.elevation).toString()
                                            : Number(bh.elevation).toFixed(1)) + ' m'}
                                    </text>
                                )}

                                {/* Drill path */}
                                <path d={pathD} stroke="var(--text-color)" strokeWidth={drillPathStroke}
                                      vectorEffect="non-scaling-stroke"
                                      fill="none" pointerEvents="none"/>

                                {/* Colored borehole segments — sorted ascending so highest renders on top.
                                    With both sources active, each source paints one half of the borehole. */}
                                {[...finalBars].sort((a, b) => a.amount.value - b.amount.value).map(bar => {
                                    const fullStroke = bar.isSelected ? (boreholeStroke + 2) : boreholeStroke;
                                    if (!bothSources) {
                                        return (
                                            <line key={`seg-${bar.id}`}
                                                  data-bar-id={bar.id}
                                                  x1={bar.p1.svgX} y1={bar.p1.svgY} x2={bar.p2.svgX} y2={bar.p2.svgY}
                                                  stroke={bar.boreholeColor}
                                                  strokeWidth={fullStroke}
                                                  vectorEffect="non-scaling-stroke"
                                                  style={{cursor: 'pointer'}}
                                            />
                                        );
                                    }
                                    const x1 = bar.p1.svgX, y1 = bar.p1.svgY, x2 = bar.p2.svgX, y2 = bar.p2.svgY;
                                    const segVecX = x2 - x1;
                                    const segVecY = y2 - y1;
                                    const segLen = Math.sqrt(segVecX ** 2 + segVecY ** 2) || 1;
                                    const perpX = segVecY / segLen;
                                    const perpY = -segVecX / segLen;
                                    const sourceIdx = dataSources.indexOf(bar.meaSource);
                                    const directionFactor = sourceIdx === 0 ? -1 : 1;
                                    const fpX = perpX * directionFactor;
                                    const fpY = perpY * directionFactor;
                                    const halfW = (fullStroke / 2) / transform.scale;
                                    const pts = [
                                        [x1, y1],
                                        [x2, y2],
                                        [x2 + halfW * fpX, y2 + halfW * fpY],
                                        [x1 + halfW * fpX, y1 + halfW * fpY],
                                    ].map(([px, py]) => `${px},${py}`).join(' ');
                                    return (
                                        <polygon key={`seg-${bar.id}`}
                                                 data-bar-id={bar.id}
                                                 points={pts}
                                                 fill={bar.boreholeColor}
                                                 style={{cursor: 'pointer'}}
                                        />
                                    );
                                })}

                                {/* Path length labels — gated by zoom level + label stride */}
                                {showPathLengthLabels && showLabel && (() => {
                                    const steps: number[] = [];
                                    for (let d = stepSize; d <= maxDepth; d += stepSize) steps.push(d);
                                    return steps.map(d => {
                                        const pos = interpolateAlongDrillPathByDepth(dripos, d);
                                        if (!pos) return null;
                                        const p3d = project(pos.relX, pos.relY, pos.relZ, factor);
                                        const label = d % 1 === 0 ? d.toString() : d.toFixed(1);
                                        return (
                                            <text key={`pl-${d}`}
                                                  x={p3d.svgX - 18 / transform.scale} y={p3d.svgY}
                                                  fill="var(--text-color-secondary)" fontSize={10 / transform.scale}
                                                  dominantBaseline="middle" textAnchor="end"
                                                  pointerEvents="none">
                                                {label + 'm'}
                                            </text>
                                        );
                                    });
                                })()}
                            </g>
                        );
                    })}
                </g>
            </svg>

            {/* Tooltip */}
            {tooltip && (
                <div style={{
                    position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 10,
                    background: 'var(--surface-overlay)', color: 'var(--text-color)',
                    padding: '6px 10px', borderRadius: 6, fontSize: 12,
                    pointerEvents: 'none', zIndex: 9999,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                }}>
                    {tooltip.content}
                </div>
            )}
        </div>
    );
};

export default CanvasContent;
